import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeftIcon, ArrowTopRightOnSquareIcon, CheckIcon, EyeIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { openFile } from "../../utils/openFile";
import { Button, Card, ConfirmModal, Container, EmptyState, Loading, PageHeader, RowAction, SectionTitle, Select, Tag, Textarea } from "../ui";
import { abroadStudentApi } from "../../api/abroadLoans";
import { errorMessageOf, fieldErrorsOf, problemMessage, problemOf } from "../../api/scholarships";
import { countryName, currencyOptionsOf, formatKigaliDate, useEducationLevels, useFieldsOfStudy, useOptions } from "../scholarships/model";
import { COURSE_SECTORS, formatDateTime } from "../scholarships/review/model";
import { AMOUNTS } from "../scholarships/funding";
import EligibilityNote, { PROFILE_PATH } from "../scholarships/student/EligibilityNote";
import { closesIn, studentStatusOf, useAcademicProfile } from "../scholarships/student/model";
import { AdmissionFields, DeclarationCard, EmploymentFields, FundingFields, PreviewModal, RequirementRow, answerRowsOf } from "../scholarships/student/ApplicationSections";
import { programmeLabel } from "./model";
import { Studies } from "./ApplicationSummary";
import { LoanCard, TravelCard } from "../scholarships/student/LoanCards";
import MessageThread from "../scholarships/loans/MessageThread";
import { loanOf, myAwardKey, studentAwardActions, studentThreadOf, useMyAward } from "./awards";
import {
  ABROAD_STUDENT_KEY,
  ELIGIBILITY_LABELS,
  backgroundOf,
  formBodyOf,
  formOf,
  myAbroadApplicationKey,
  rulesSummary,
  useAbroadConsent,
  useMyAbroadApplication,
  useStudentAbroadCall,
} from "./student";

// The studies the API needs on submit, all from the academic profile
const STUDY_FIELDS = new Set(["currentLevelId", "fieldOfStudyId", "gradePercent", "completionYear"]);
const PROFILE_MESSAGE = "Complete your academic profile: your level, field of study, final grade and year completed.";

// A step of the history as the student reads it: "LOAN_ACCEPTED" → "Loan accepted"
// FORWARDED_TO_DG reads "Forwarded to DG"; the offices' acronyms stay in capitals

// Submit's problems by where they show: a form field, a document (`requirement:<id>`) or the whole application
const problemsOf = (problem) =>
  Object.fromEntries(
    problem.errors.map((e) => {
      if (STUDY_FIELDS.has(e.field)) return ["profile", PROFILE_MESSAGE];
      return [e.field ?? e.code, problemMessage(e.code, "abroad-loans") ?? "This is not valid."];
    })
  );

/**
 * A student's abroad loan application. While it is a draft they fill in the form (their studies come
 * from the academic profile), upload the documents their programme needs, agree to the declaration
 * check a preview and submit, or cancel it. Once sent they may take it back to change it until the
 * deadline, or withdraw it while it is undecided; its history is on the applications list.
 */
export default function AbroadApplicationPage() {
  const { applicationId } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: application, isPending, isError, refetch } = useMyAbroadApplication(applicationId);
  const { data: call, isError: callGone } = useStudentAbroadCall(application?.callId);
  const { data: consent } = useAbroadConsent();
  const { data: profile } = useAcademicProfile();
  const { data: options } = useOptions();
  const { data: levels = [] } = useEducationLevels();
  const { data: fields = [] } = useFieldsOfStudy();
  // Once the Minister approves: the student's answer, travel details and messages with BRD
  const { data: award } = useMyAward(applicationId, Boolean(application?.letterReference));

  const [form, setForm] = useState(null);
  const [programmeId, setProgrammeId] = useState("");
  const [savingForm, setSavingForm] = useState(false);
  const [uploading, setUploading] = useState(null); // requirement ID
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [problems, setProblems] = useState({}); // field → message, from a refused save or submit
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reopening, setReopening] = useState(false);

  useEffect(() => {
    if (application && form === null) {
      setForm(formOf(application));
      setProgrammeId(application.programme.id);
    }
  }, [application, form]);

  const editable = application?.studentStatus === "DRAFT";
  // What a save sends, and what is saved: the studies always come from the current profile
  const body = useMemo(() => form && application && formBodyOf(form, programmeId, backgroundOf(editable ? profile : null, application.background)), [form, programmeId, profile, application, editable]);
  const savedBody = useMemo(() => application && formBodyOf(formOf(application), application.programme.id, backgroundOf(null, application.background)), [application]);

  if (isPending || (application && form === null)) return <Loading label="Loading your application…" />;
  if (isError) return <EmptyState title="Could not load this application" onRefresh={refetch} />;

  const status = studentStatusOf(
    application.studentStatus,
    award && { paid: award.tuitionPaid, due: award.approvedAmounts?.tuitionFee, currency: award.approvedAmounts?.currency }
  );
  const offered = call?.programmes.find((p) => p.programme.id === programmeId);
  const requirements = offered?.requirements ?? [];
  const formChanged = JSON.stringify(body) !== JSON.stringify(savedBody);
  const reload = () => queryClient.invalidateQueries({ queryKey: myAbroadApplicationKey(applicationId) });
  const awardSaved = (fresh) => {
    queryClient.setQueryData(myAwardKey(applicationId), fresh);
    // The answer and the travel details move the application's status
    reload();
    queryClient.invalidateQueries({ queryKey: [...ABROAD_STUDENT_KEY, "applications"] });
  };
  const saved = (data) => {
    queryClient.setQueryData(myAbroadApplicationKey(applicationId), data);
    queryClient.invalidateQueries({ queryKey: [...ABROAD_STUDENT_KEY, "applications"] });
  };

  const saveForm = async ({ quiet = false } = {}) => {
    setSavingForm(true);
    try {
      const { data } = await abroadStudentApi.saveForm(applicationId, body);
      saved(data);
      if (!quiet) toast.success("Saved");
      return true;
    } catch (error) {
      console.error(`Failed to save abroad loan application ${applicationId}:`, error);
      setProblems((prev) => ({ ...prev, ...fieldErrorsOf(problemOf(error)) }));
      toast.error(errorMessageOf(error, "Failed to save. Please try again."));
      if (problemOf(error)?.code === "changed-meanwhile") reload();
      return false;
    } finally {
      setSavingForm(false);
    }
  };

  const upload = async (requirement, file) => {
    // A document belongs to the saved programme: save a programme change first
    if (programmeId !== application.programme.id && !(await saveForm({ quiet: true }))) return;
    setUploading(requirement.id);
    setProblems((prev) => ({ ...prev, [`requirement:${requirement.id}`]: undefined }));
    try {
      await abroadStudentApi.upload(applicationId, requirement.id, file);
      toast.success(`${requirement.label} uploaded`);
    } catch (error) {
      console.error(`Failed to upload ${requirement.code} to abroad loan application ${applicationId}:`, error);
      const message = errorMessageOf(error, "Upload failed. Please try again.");
      setProblems((prev) => ({ ...prev, [`requirement:${requirement.id}`]: message }));
      toast.error(message);
    } finally {
      setUploading(null);
      reload();
    }
  };

  const removeDocument = async (document) => {
    try {
      await abroadStudentApi.removeDocument(applicationId, document.id);
      toast.success("Document removed");
    } catch (error) {
      console.error(`Failed to remove document ${document.id}:`, error);
      toast.error(errorMessageOf(error, "Failed to remove the document. Please try again."));
    } finally {
      reload();
    }
  };

  const openDocument = (document) => openFile(async () => (await abroadStudentApi.document(applicationId, document.id)).data, document.fileName);
  const openLegacy = (document) => openFile(async () => (await abroadStudentApi.legacyDocument(applicationId, document.id)).data, document.fileName);

  const submit = async () => {
    setProblems({});
    if (formChanged && !(await saveForm({ quiet: true }))) return;
    setSubmitting(true);
    try {
      const { data } = await abroadStudentApi.submit(applicationId, consent.version);
      saved(data);
      setPreviewing(false);
      toast.success("Application submitted. Good luck!");
    } catch (error) {
      const problem = problemOf(error);
      console.error(`Failed to submit abroad loan application ${applicationId}:`, error);
      if (problem?.code === "submission-invalid") {
        setPreviewing(false);
        setProblems(problemsOf(problem));
        toast.error("Your application is not complete yet. See the highlighted parts.");
      } else {
        toast.error(errorMessageOf(error, "Failed to submit. Please try again."));
      }
      if (problem?.code === "changed-meanwhile") reload();
    } finally {
      setSubmitting(false);
    }
  };

  // The preview shows what is saved, so pending answers are saved first
  const preview = async () => {
    setProblems({});
    if (formChanged && !(await saveForm({ quiet: true }))) return;
    setPreviewing(true);
  };

  // An unsent application is cancelled: it disappears and the student may start again
  const cancel = async () => {
    setSubmitting(true);
    try {
      await abroadStudentApi.cancel(applicationId);
      queryClient.invalidateQueries({ queryKey: [...ABROAD_STUDENT_KEY, "applications"] });
      toast.success("Application cancelled");
      navigate("/my-applications");
    } catch (error) {
      console.error(`Failed to cancel abroad loan application ${applicationId}:`, error);
      toast.error(errorMessageOf(error, "Failed to cancel. Please try again."));
      reload();
    } finally {
      setSubmitting(false);
      setCancelling(false);
    }
  };

  // A sent application back to a draft, to change it before the deadline
  const reopen = async () => {
    setSubmitting(true);
    try {
      const { data } = await abroadStudentApi.reopen(applicationId);
      saved(data);
      setAgreed(false);
      toast.success("You can change your application now. Submit it again before the deadline.");
    } catch (error) {
      console.error(`Failed to reopen abroad loan application ${applicationId}:`, error);
      toast.error(errorMessageOf(error, "Failed to open your application for changes. Please try again."));
      reload();
    } finally {
      setSubmitting(false);
      setReopening(false);
    }
  };

  const withdraw = async () => {
    setSubmitting(true);
    try {
      const { data } = await abroadStudentApi.withdraw(applicationId, withdrawReason.trim());
      saved(data);
      setWithdrawing(false);
      toast.success("Application withdrawn");
    } catch (error) {
      console.error(`Failed to withdraw abroad loan application ${applicationId}:`, error);
      toast.error(errorMessageOf(error, "Failed to withdraw. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  // Funding fields are top-level in this API, so a field's problem has its own name
  const field = (name) => ({
    name,
    value: form[name],
    onChange: (e) => {
      setProblems((prev) => ({ ...prev, [name]: undefined }));
      setForm((prev) => ({ ...prev, [name]: e.target.value }));
    },
    error: problems[name],
    disabled: !editable || savingForm,
  });

  const loan = award && loanOf(award, application);
  const awardActions = award && studentAwardActions(applicationId, application.letterReference, awardSaved);

  // The kinds the saved studies miss, with the programme's rules of those kinds
  const unmet = application.unmetEligibility ?? [];
  const unmetRules = offered ? rulesSummary(offered.eligibility.filter((r) => unmet.includes(r.kind))) : unmet.map((kind) => ELIGIBILITY_LABELS[kind]);
  const general = [problems["call-closed"], problems.profile].filter(Boolean);
  const place = [application.admission?.institution, application.admission?.countryCode && countryName(application.admission.countryCode)].filter(Boolean).join(", ");

  return (
    <Container className="flex flex-col gap-6 py-6">
      <Link to="/my-applications" className="flex w-fit items-center gap-1 text-sm font-medium text-brand hover:underline">
        <ArrowLeftIcon className="h-4 w-4" /> My applications
      </Link>
      <PageHeader
        title={application.callName}
        subtitle={`${application.admission?.programme || application.programme.name} · ${place || "where you were admitted: not given yet"}`}
        actions={<Tag color={status.color}>{status.label}</Tag>}
      />

      <div className={`rounded-lg p-4 text-sm ${status.color === "green" ? "bg-green-50 text-green-800" : "bg-slate-50 text-slate-700"}`}>
        <p>{status.hint}</p>
        {application.decisionReason && <p className="mt-1 italic">“{application.decisionReason}”</p>}
        {editable && (
          <p className="mt-1 font-medium text-amber-700">
            Deadline {formatKigaliDate(application.closesAt)}, 23:59 Kigali time · {closesIn(application.closesAt)}
          </p>
        )}
        {application.submittedAt && <p className="mt-1">Submitted {formatDateTime(application.submittedAt)}</p>}
      </div>

      {award && (
        <>
          <LoanCard applicationId={applicationId} loan={loan} travels actions={awardActions} />
          {award.accepted && (
            <>
              <TravelCard applicationId={applicationId} travel={loan.travel} editable={!award.ticket} actions={awardActions} />
              <Card title="Messages with BRD">
                <MessageThread {...studentThreadOf(applicationId)} side="STUDENT" />
              </Card>
            </>
          )}
        </>
      )}

      <Card title="1. About you">
        <SectionTitle title="Your studies" />
        <div className="mb-6 mt-3 flex flex-col gap-3">
          <Studies draft={editable} profile={profile} background={application.background} levels={levels} fields={fields} />
          {editable && unmetRules.length > 0 && <EligibilityNote eligibility={{ eligible: false, unmetRules: unmetRules.map((label) => ({ label })) }} />}
          {editable && profile && (
            <p className="text-xs text-slate-500">
              From your{" "}
              <Link to={PROFILE_PATH} className="font-medium text-brand hover:underline">
                academic profile
              </Link>
              ; changes there apply to this application until you submit it.
            </p>
          )}
        </div>
        <SectionTitle title="About this application" />
        <div className="mt-3 grid gap-4 md:grid-cols-2">
          {editable && call && call.programmes.length > 1 && (
            <Select
              label="Programme"
              required
              value={programmeId}
              onChange={(e) => setProgrammeId(e.target.value)}
              options={call.programmes.map(({ programme: p }) => ({ value: p.id, label: programmeLabel(p) }))}
              disabled={savingForm}
              info="Documents this programme also asks for are kept"
              className="md:col-span-2"
            />
          )}
          <AdmissionFields field={field} abroad />
          <Select label="Priority sector of your studies" required placeholder="Choose a sector" options={COURSE_SECTORS} {...field("courseSector")} />
          <EmploymentFields form={form} field={field} />
        </div>
      </Card>

      <Card title="2. Funding you need">
        <FundingFields
          form={form}
          field={field}
          started={{ value: form.started, onChange: (e) => setForm((prev) => ({ ...prev, started: e.target.value })), disabled: !editable || savingForm }}
          currencyOptions={currencyOptionsOf(options?.currencies, application.loan?.currency)}
          amounts={AMOUNTS}
        />
      </Card>

      <Card title="3. Documents">
        {offered ? (
          <ul className="flex flex-col divide-y divide-slate-100">
            {requirements.map((r) => (
              <RequirementRow
                key={r.id}
                requirement={r}
                document={(() => {
                  const d = application.documents.find((doc) => doc.requirementId === r.id);
                  return d && { ...d, detectedType: d.mediaType };
                })()}
                editable={editable}
                busy={uploading === r.id}
                error={problems[`requirement:${r.id}`]}
                onUpload={upload}
                onDelete={removeDocument}
                onOpen={openDocument}
              />
            ))}
          </ul>
        ) : callGone ? (
          // The call is no longer published: the files are still the student's to open
          <ul className="flex flex-col divide-y divide-slate-100">
            {application.documents.map((d) => (
              <li key={d.id} className="flex items-center justify-between py-3 text-sm">
                <span className="truncate text-slate-800">{d.fileName}</span>
                <RowAction Icon={ArrowTopRightOnSquareIcon} label="Open" variant="ghost" onClick={() => openDocument(d)} />
              </li>
            ))}
          </ul>
        ) : (
          <Loading label="Loading the documents…" />
        )}
        {application.legacyDocuments?.length > 0 && (
          <div className="mt-4">
            <SectionTitle title="From the former system" />
            <ul className="mt-2 flex flex-col divide-y divide-slate-100">
              {application.legacyDocuments.map((d) => (
                <li key={d.id} className="flex items-center justify-between py-3 text-sm">
                  <span className="truncate text-slate-800">{d.fileName}</span>
                  <RowAction Icon={ArrowTopRightOnSquareIcon} label="Open" variant="ghost" onClick={() => openLegacy(d)} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      {editable && consent && (
        <DeclarationCard
          title="4. Declaration and submit"
          consent={consent}
          agreed={agreed}
          onAgree={setAgreed}
          error={problems.consentVersion}
          messages={general}
          onSubmit={preview}
          submitting={savingForm}
          submitLabel="Preview and submit"
          submitIcon={<EyeIcon className="h-4 w-4" />}
        />
      )}

      {/* Unsent: cancel it; sent: change it until the deadline, or withdraw it until the outcome */}
      {(editable || application.reopenable || application.withdrawable) && (
        <div className="flex flex-wrap justify-end gap-3">
          {editable && (
            <Button variant="danger-soft" onClick={() => setCancelling(true)}>
              Cancel application
            </Button>
          )}
          {/* One save for the whole form; previewing saves it too */}
          {editable && (
            <Button variant="outline" onClick={() => saveForm()} loading={savingForm} disabled={!formChanged} icon={<CheckIcon className="h-4 w-4" />}>
              Save draft
            </Button>
          )}
          {!editable && application.withdrawable && (
            <Button variant="danger-soft" onClick={() => setWithdrawing(true)}>
              Withdraw application
            </Button>
          )}
          {application.reopenable && (
            <Button variant="outline" icon={<PencilSquareIcon className="h-4 w-4" />} onClick={() => setReopening(true)}>
              Edit application
            </Button>
          )}
        </div>
      )}

      {previewing && (
        <PreviewModal
          subtitle={`${application.callName} · ${offered ? programmeLabel(offered.programme) : application.programme.name}`}
          studies={<Studies draft profile={profile} background={application.background} levels={levels} fields={fields} />}
          answers={answerRowsOf(form, { admission: true, loan: true, amounts: AMOUNTS })}
          requirements={requirements}
          hasDocument={(r) => application.documents.some((d) => d.requirementId === r.id)}
          closesAt={application.closesAt}
          submitting={submitting}
          onBack={() => setPreviewing(false)}
          onSubmit={submit}
        />
      )}
      <ConfirmModal
        open={cancelling}
        onClose={() => setCancelling(false)}
        onConfirm={cancel}
        loading={submitting}
        destructive
        title="Cancel your application?"
        description="It will be removed from your applications, with the documents you uploaded. While the loan call is open you can start a new one."
        confirmLabel="Cancel application"
        cancelLabel="Keep it"
      />
      <ConfirmModal
        open={reopening}
        onClose={() => setReopening(false)}
        onConfirm={reopen}
        loading={submitting}
        title="Change your application?"
        description={`It goes back to a draft so you can change it. Submit it again before ${formatKigaliDate(application.closesAt)}, 23:59 Kigali time, or it will not be considered.`}
        confirmLabel="Change it"
      />

      <ConfirmModal
        open={withdrawing}
        onClose={() => setWithdrawing(false)}
        onConfirm={withdraw}
        loading={submitting}
        destructive
        title="Withdraw your application?"
        description="HEC will no longer consider it. While the loan call is still open you can start a new one."
        confirmLabel="Withdraw"
      >
        <Textarea label="Reason (optional)" rows={2} maxLength={500} value={withdrawReason} onChange={(e) => setWithdrawReason(e.target.value)} />
      </ConfirmModal>
    </Container>
  );
}
